import React, { useCallback, useEffect, useId, useState } from 'react';
import { RiArrowRightSLine } from 'react-icons/ri';
import { useNavigate, useParams } from 'react-router-dom';
import axiosInstance from 'config/axiosInstance';
import { businessDateRange, toBusinessDate } from 'config/time';
import { IconEdit, IconTrash } from '@tabler/icons-react';
import user_icon from '../../../assets/icons/user.svg';
import TaskSlotGrid from '../../../reusable/Client/TaskSlotGrid';
import DeleteClientDialog from '../../../reusable/Client/dialog/DeleteClientDialog';
import {
  formatTotalDuration,
  secondsToHoursAndMinutes,
  timeStringToSeconds,
} from '../Utility/timeHelpers';
import { useTranslation } from 'react-i18next';
import FetchFailed from 'reusable/FetchFailed';
import { useLatestRequest } from 'reusable/useLatestRequest';

interface ClientData {
  name: string;
  description: string | null;
  tasks: string[];
  assign_drivers: string[];
}

interface DeleteClientData {
  id: string;
  clientName: string;
  assign_drivers: string;
  tasks: string;
}

interface DriverData {
  name: string;
  profile_picture: string | null;
}

interface Task {
  id: number;
  name: string;
  icon: string;
}

/** ADM-08。当年の1月1日〜12月31日を業務日で表す。 */
const currentYearRange = () => {
  const year = toBusinessDate(new Date()).slice(0, 4);
  return businessDateRange(`${year}-01-01`, `${year}-12-31`);
};

const ViewClient: React.FC = () => {
  const { t } = useTranslation(['client']);
  const [data, setData] = useState<ClientData | null>(null);
  const [loading, setLoading] = useState<boolean>(true);
  // ADM-15。取得の失敗（失敗の種類と再読み込みを出す）。以前は英語の固定文字だけだった。
  const [error, setError] = useState<unknown>(null);
  const [taskDetails, setTaskDetails] = useState<Task[]>([]);
  const [driverNames, setDriverNames] = useState<DriverData[]>([]);
  const [totalDurationWithTasks, setTotalDurationWithTasks] = useState<{
    hours: number;
    minutes: number;
  } | null>(null);
  const [showDeleteClientDialog, setShowDeleteClientDialog] =
    useState<boolean>(false);
  const [deleteClient, setDeleteClient] = useState<DeleteClientData | null>(
    null
  );
  const navigate = useNavigate();
  const { id } = useParams<{ id: string }>();

  const handleEditClick = () => {
    navigate(`/client/${id}/edit`);
  };

  const handleDeleteClick = () => {
    if (data && id !== undefined) {
      setDeleteClient({
        id: id,
        clientName: data.name,
      assign_drivers: t('table.driver_count', { count: data.assign_drivers.length }),
        tasks: data.tasks.length.toString(),
      });
      setShowDeleteClientDialog(true);
    } else {
      console.error('Client ID is undefined.');
    }
  };

  // EV-322（ADM-15）。今年の合計時間を読めなかったこと（読み込み中と同じ「-」と区別し、欄の注記で出す）。
  // 最後の要求の応答だけを反映する（配送先を切り替えた後・画面を離れた後の応答は捨てる）。
  const [totalDurationError, setTotalDurationError] = useState<unknown>(null);
  const beginTotalRequest = useLatestRequest();
  const totalDurationNoteId = useId();

  const fetchTotalDuration = useCallback(async () => {
    const isCurrent = beginTotalRequest();
    setTotalDurationError(null);
    try {
      const response = await axiosInstance.get(
        `/v1/client_deliveries/summarize_spent_hour_with_tasks/?id__in=${id}` +
          // ADM-08。2024年固定ではなく、要求時点の年を対象にする。
          `&start_time=${encodeURIComponent(currentYearRange().gte ?? '')}` +
          `&stop_time=${encodeURIComponent(currentYearRange().lte ?? '')}`
      );
      const hourSpentArray = response.data[0]?.summarize || [];
      const totalSeconds = hourSpentArray.reduce(
        (total: number, entry: any) => {
          return total + timeStringToSeconds(entry.hour_spent);
        },
        0
      );

      if (!isCurrent()) return;
      // EV-115。0時間も値として持つ（null は読み込み中・取得できないときだけ）。
      setTotalDurationWithTasks(secondsToHoursAndMinutes(totalSeconds));
    } catch (error) {
      if (!isCurrent()) return;
      console.error('Failed to fetch total duration', error);
      setTotalDurationWithTasks(null);
      setTotalDurationError(error);
    }
  }, [id, beginTotalRequest]);

  const fetchData = useCallback(async () => {
    setLoading(true);
    try {
      const response = await axiosInstance.get(`/v1/client_deliveries/${id}/`);
      setData(response.data);

      const taskResponses = await Promise.all(
        response.data.tasks.map((taskId: string) =>
          axiosInstance.get(`/v1/tasks/${taskId}/`)
        )
      );
      setTaskDetails(
        taskResponses.map((res) => ({
          id: res.data.id,
          name: res.data.name,
          icon: res.data.icon || 'default',
        }))
      );

      const driverResponses = await Promise.all(
        response.data.assign_drivers.map((driverId: string) =>
          axiosInstance.get(`/v1/drivers/${driverId}/`)
        )
      );
      setDriverNames(
        driverResponses.map((res) => ({
          name: res.data.name,
          profile_picture: res.data.profile_picture || null,
        }))
      );

      setError(null);
      fetchTotalDuration(); // Fetch total duration after fetching client data
    } catch (error) {
      console.error('Failed to fetch client data', error);
      setError(error);
    } finally {
      setLoading(false);
    }
  }, [id, fetchTotalDuration]);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  if (loading) {
    return <div>{t('ui.loading', { ns: 'common' })}</div>;
  }

  if (error) {
    return (
      <FetchFailed
        error={error}
        onRetry={fetchData}
        message={t('ui.detail-fetch-failed', { ns: 'common' })}
      />
    );
  }

  if (!data) {
    return <div>{t('ui.no-client-data', { ns: 'client' })}</div>;
  }

  return (
    <section className="flex flex-col p-4 space-y-4 h-screen overflow-y-auto">
      <div className="flex flex-row items-center gap-4 text-primary/300 font-medium text-subtitle-1">
        <h6>{t('manage_client.title')}</h6>
        <RiArrowRightSLine size={24} />
        <h6>{id}</h6>
        <RiArrowRightSLine size={24} />
        <h6 className="underline">{t('manage_client.detail')}</h6>
      </div>
      {/* Body Section */}
      <div className="flex flex-col rounded-md bg-white flex-grow">
        <div className="flex flex-col px-6 py-4 gap-4">
          <div className="flex justify-between">
            <h6 className="font-medium text-subtitle-1">
              {t('manage_client.client_info')}
            </h6>
            <div className="flex flex-row items-end gap-2 text-subtitle-2 text-neutral/100">
              <button type="button"
                className="flex flex-row items-center gap-2 px-2 py-3 rounded"
                onClick={handleEditClick}
              >
                <IconEdit stroke={1.5} size={20} />
                <p>{t('action.edit')}</p>
              </button>
              <button type="button"
                className="flex flex-row items-center gap-2 px-2 py-3 rounded"
                onClick={handleDeleteClick}
              >
                <IconTrash stroke={1.5} size={20} />
                <p>{t('action.delete')}</p>
              </button>
            </div>
          </div>
          <div className="grid grid-cols-5">
            <div className="flex flex-col col-span-2 gap-6">
              <div className="flex flex-col gap-1">
                <label className="text-caption text-neutral/100">
                  {t('detail.client_name')} ({data.name.length}/30)
                </label>
                <p className=" h-10 border border-neutral/50 rounded py-2 px-4 text-neutral/300 text-caption">
                  {data.name}
                </p>
              </div>
              <div className="flex flex-col gap-1">
                <label className="text-caption text-neutral/100">
                  {t('detail.client_description')} ({(data.description ?? '').length}
                  /100)
                </label>

                <p className="h-20 border border-neutral/50 text-neutral/300 text-caption rounded py-2 px-4">
                  {data.description || '\u00A0'}
                </p>
              </div>
            </div>
            <div className="flex flex-row col-span-3 gap-10 pl-24">
              <div className="flex flex-col w-full gap-1">
                <label
                  htmlFor="totalDuration"
                  className="text-caption text-neutral/100"
                >
                  {t('detail.total_duration')}
                </label>
                <p
                  className="text-medium text-h5 text-neutral/300"
                  aria-describedby={totalDurationError ? totalDurationNoteId : undefined}
                >
                  {/* ADM-21 / EV-115。単位は画面の言語。0時間は分、読み込み中・失敗は「-」。 */}
                  {formatTotalDuration(t, totalDurationWithTasks)}
                </p>
                {/* UAT A09-1（和田様）。数の意味（数え方）を小さく添える。 */}
                <p className="text-caption text-neutral/100">{t('detail.total_duration_hint')}</p>
                {totalDurationError ? (
                  <FetchFailed
                    id={totalDurationNoteId}
                    compact
                    error={totalDurationError}
                    onRetry={fetchTotalDuration}
                    message={t('ui.detail-fetch-failed', { ns: 'common' })}
                  />
                ) : null}
              </div>
              <div className="flex flex-col w-full gap-1">
                <label
                  htmlFor="activeTasks"
                  className="text-caption text-neutral/100"
                >
                  {t('detail.active_task')}
                </label>
                <p className="text-medium text-h5 text-neutral/300">
                  {data.tasks.length}
                </p>
                <p className="text-caption text-neutral/100">{t('detail.active_task_hint')}</p>
              </div>
              <div className="flex flex-col w-full gap-1">
                <label
                  htmlFor="driverAssigned"
                  className="text-caption text-neutral/100"
                >
                  {t('detail.driver_assigned')}
                </label>
                <p className="text-medium text-h5 text-neutral/300">
                  {data.assign_drivers.length}
                </p>
                <p className="text-caption text-neutral/100">{t('detail.driver_assigned_hint')}</p>
              </div>
            </div>
          </div>
        </div>
        <hr />
        <div className="flex flex-col px-6 py-4 gap-4">
          <h4 className="font-medium text-subtitle-1"> {t('detail.tasks')}</h4>
          {/* A09-3。登録・編集・タスク管理と同じ 3行×5列の箱の並び（見るだけ。空の枠は出さない）。 */}
          <TaskSlotGrid tasks={taskDetails} removeLabel={(task) => task.name} />
        </div>
        <hr />
        <div className="flex flex-col px-6 py-4 gap-4">
          <h4 className="font-medium text-subtitle-1">
            {t('detail.driver_assignments')} - {driverNames.length}{' '}
            {t('detail.drivers')}
          </h4>
          <div className="grid grid-cols-5 gap-3">
            {driverNames.map((driver, index) => (
              <div
                key={index}
                className="flex flex-row justify-between items-center border rounded-md h-16 px-3 gap-2"
              >
                <img
                  src={driver.profile_picture || user_icon}
                  alt=""
                  className="h-10 w-10 object-cover rounded-full"
                />
                <span className="w-full truncate text-body-2">
                  {' '}
                  {/* EV-314。名前の空は「-」（EV-311 の表の空と同じ）。 */}
                  {driver.name || '-'}
                </span>
              </div>
            ))}
          </div>
        </div>
      </div>
      {showDeleteClientDialog && deleteClient && (
        <DeleteClientDialog
          id={deleteClient.id}
          name={deleteClient.clientName}
          isOpen={showDeleteClientDialog}
          onClose={() => setShowDeleteClientDialog(false)}
          fields={[
            { label: t('table.client_id'), value: deleteClient.id },
            { label: t('table.client_name'), value: deleteClient.clientName },
            {
              label: t('table.assignees'),
              value: deleteClient.assign_drivers,
            },
            { label: t('table.task_counts'), value: deleteClient.tasks },
          ]}
        />
      )}
    </section>
  );
};

export default ViewClient;
